import { useEffect, useId, useRef, useState } from "react";
import type { EstimatorInput, EstimatorResult } from "@reno-estimator/core";
import {
  submitLead,
  SubmissionGuard,
  type LeadCta,
  type PhotoUploadCandidate
} from "@reno-estimator/leads";
import { getLeadStore, getLeadRateLimiter, isLeadDeliveryConfigured } from "../../engine/leadStore.js";
import { getAnalyticsClient } from "../../engine/analyticsClient.js";
import { OptionCard } from "../ui/OptionCard.js";

const PREFERRED_CONTACT_OPTIONS: { id: string; label: string }[] = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "either", label: "Either" }
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  preferredContactMethod: string;
  postalCode: string;
  desiredStartDate: string;
  projectDescription: string;
  /** Honeypot - real users never see or fill this; any value here means a bot. Deliberately not named "email"/"phone"/"company"/"website" so autofill tools (which match common field-name patterns) don't fill it for a real, legitimate user. */
  hpField: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  email: "",
  phone: "",
  preferredContactMethod: "",
  postalCode: "",
  desiredStartDate: "",
  projectDescription: "",
  hpField: ""
};

type SubmitOutcome = "idle" | "submitting" | "saved" | "invalid" | "duplicate" | "rejected_spam" | "rate_limited";

async function readFileAsPhotoCandidate(file: File): Promise<PhotoUploadCandidate> {
  const data = new Uint8Array(await file.arrayBuffer());
  return { fileName: file.name, declaredMimeType: file.type, sizeBytes: file.size, data };
}

/**
 * The Phase 16 lead capture form. Deliberately rendered by the caller only
 * after the full estimate is already visible (see Step7Estimate.tsx) - per
 * the brief, this never gates seeing the basic estimate on providing
 * contact information.
 */
export function LeadCaptureForm({ input, result, source }: { input: EstimatorInput; result: EstimatorResult; source?: string }) {
  const formRenderedAtRef = useRef(new Date().toISOString());
  const submissionGuardRef = useRef(new SubmissionGuard());

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [photos, setPhotos] = useState<File[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<SubmitOutcome>("idle");
  const [lastCta, setLastCta] = useState<LeadCta | null>(null);

  useEffect(() => {
    getAnalyticsClient().track("quote_request_started");
    // Fire once, when the form is first shown alongside the estimate - not once per CTA click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const postalId = useId();
  const startDateId = useId();
  const descriptionId = useId();
  const photosId = useId();

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(cta: LeadCta) {
    if (!submissionGuardRef.current.start()) return;
    setOutcome("submitting");
    setFieldErrors({});
    setLastCta(cta);

    try {
      const photoCandidates = await Promise.all(photos.map(readFileAsPhotoCandidate));
      const result_ = await submitLead({
        store: getLeadStore(),
        cta,
        input,
        result,
        rawForm: {
          name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          preferredContactMethod: form.preferredContactMethod,
          postalCode: form.postalCode,
          desiredStartDate: form.desiredStartDate || undefined,
          projectDescription: form.projectDescription || undefined
        },
        honeypotValue: form.hpField,
        formRenderedAt: formRenderedAtRef.current,
        photos: photoCandidates.length > 0 ? photoCandidates : undefined,
        source,
        rateLimiter: getLeadRateLimiter(),
        rateLimitKey: (form.email || form.phone || "unknown").trim().toLowerCase()
      });

      if (result_.status === "invalid") {
        const errors: Record<string, string> = {};
        for (const e of result_.errors) errors[e.field] = e.message;
        setFieldErrors(errors);
        setOutcome("invalid");
      } else {
        setOutcome(result_.status);
        if (result_.status === "saved") {
          getAnalyticsClient().track(cta === "email_me_my_estimate" ? "email_estimate_requested" : "quote_request_submitted", {
            source: "lead_form"
          });
        }
      }
    } finally {
      submissionGuardRef.current.finish();
    }
  }

  if (outcome === "saved") {
    // Audit fix (Phase 23, Full Application Red-Team Audit Finding 1 -
    // Critical): this used to always show the "someone will follow up" /
    // "we'll email you" copy below, even when nothing was actually wired
    // up to receive it (see leadStore.ts/emailEstimateClient.ts's Phase 23
    // fixes) - the confirmation was false in exactly the deployments where
    // it mattered most. Only claim a real follow-up when a real delivery
    // backend is actually configured (isLeadDeliveryConfigured()).
    const deliveryConfigured = isLeadDeliveryConfigured();
    return (
      <div className="estimate-card lead-capture lead-capture--success" role="status">
        <h2>Thanks, {form.name.split(" ")[0] || "there"}!</h2>
        <p>
          {deliveryConfigured
            ? lastCta === "email_me_my_estimate"
              ? "We've emailed a copy of this estimate to you - check your inbox in the next few minutes."
              : "We've received your request and saved it - our team will follow up about a detailed, on-site quote."
            : lastCta === "email_me_my_estimate"
              ? "This preview has no email delivery backend connected, so nothing was actually emailed - no one will follow up."
              : "This preview has no delivery backend connected, so this request wasn't actually saved or sent anywhere - no one will follow up."}
        </p>
      </div>
    );
  }

  if (outcome === "duplicate") {
    return (
      <div className="estimate-card lead-capture" role="status">
        <h2>We already have your request</h2>
        <p>Looks like you've already submitted this project recently - we'll be in touch soon. No need to send it again.</p>
      </div>
    );
  }

  return (
    <div className="estimate-card lead-capture">
      <h2>Want a more accurate project estimate?</h2>
      <p>Share a few details and we'll follow up - the estimate above is yours either way.</p>

      {!isLeadDeliveryConfigured() ? (
        <p className="lead-capture__dev-notice" role="note">
          Preview mode: no delivery backend is connected, so a submission here won't be saved or reach anyone.
        </p>
      ) : null}

      {outcome === "rejected_spam" ? (
        <p className="field-error" role="alert">
          We couldn't process that submission. If this seems wrong, please try again in a moment.
        </p>
      ) : null}

      {outcome === "rate_limited" ? (
        <p className="field-error" role="alert">
          Too many requests submitted recently - please wait a few minutes and try again.
        </p>
      ) : null}

      <form
        className="form-grid lead-capture__form"
        onSubmit={(e) => e.preventDefault()}
        noValidate
      >
        {/* Honeypot: hidden from real users, invisible to screen readers, never tab-focusable. Any bot that blindly fills every field will fill this too. Field name/label deliberately avoid common autofill-target words (email/phone/company/website/url) so browser and password-manager autofill doesn't fill it for a real user and trigger a false-positive spam rejection. */}
        <label className="lead-capture__honeypot" aria-hidden="true">
          Leave this field blank
          <input
            type="text"
            name="hp_field_49q"
            tabIndex={-1}
            autoComplete="off"
            value={form.hpField}
            onChange={(e) => updateField("hpField", e.target.value)}
          />
        </label>

        <label className="text-field" htmlFor={nameId}>
          <span>Name</span>
          <input id={nameId} type="text" autoComplete="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
          {fieldErrors.name ? <span className="field-error">{fieldErrors.name}</span> : null}
        </label>

        <label className="text-field" htmlFor={emailId}>
          <span>Email</span>
          <input id={emailId} type="email" autoComplete="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} />
          {fieldErrors.email ? <span className="field-error">{fieldErrors.email}</span> : null}
        </label>

        <label className="text-field" htmlFor={phoneId}>
          <span>Phone</span>
          <input id={phoneId} type="tel" autoComplete="tel" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} />
          {fieldErrors.phone ? <span className="field-error">{fieldErrors.phone}</span> : null}
        </label>

        <fieldset className="detail-field">
          <legend>Preferred contact method</legend>
          <div className="option-grid option-grid--compact" role="radiogroup" aria-label="Preferred contact method">
            {PREFERRED_CONTACT_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.id}
                name="preferred_contact_method"
                value={opt.id}
                label={opt.label}
                checked={form.preferredContactMethod === opt.id}
                onChange={(checked) => checked && updateField("preferredContactMethod", opt.id)}
              />
            ))}
          </div>
          {fieldErrors.preferredContactMethod ? <span className="field-error">{fieldErrors.preferredContactMethod}</span> : null}
        </fieldset>

        <label className="text-field" htmlFor={postalId}>
          <span>Postal code</span>
          <input
            id={postalId}
            type="text"
            autoComplete="postal-code"
            placeholder="A1A 1A1"
            value={form.postalCode}
            onChange={(e) => updateField("postalCode", e.target.value)}
          />
          {fieldErrors.postalCode ? <span className="field-error">{fieldErrors.postalCode}</span> : null}
        </label>

        <label className="text-field" htmlFor={startDateId}>
          <span>Desired start date (optional)</span>
          <input id={startDateId} type="date" value={form.desiredStartDate} onChange={(e) => updateField("desiredStartDate", e.target.value)} />
          {fieldErrors.desiredStartDate ? <span className="field-error">{fieldErrors.desiredStartDate}</span> : null}
        </label>

        <label className="text-field" htmlFor={descriptionId}>
          <span>Tell us more about your project (optional)</span>
          <textarea
            id={descriptionId}
            rows={3}
            value={form.projectDescription}
            onChange={(e) => updateField("projectDescription", e.target.value)}
          />
          {fieldErrors.projectDescription ? <span className="field-error">{fieldErrors.projectDescription}</span> : null}
        </label>

        <label className="text-field" htmlFor={photosId}>
          <span>Photos of the space (optional)</span>
          <input
            id={photosId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
          />
          {fieldErrors.photos ? <span className="field-error">{fieldErrors.photos}</span> : null}
        </label>

        <div className="lead-capture__ctas">
          <button
            type="button"
            className="btn btn--primary"
            disabled={outcome === "submitting"}
            onClick={() => void handleSubmit("request_detailed_quote")}
          >
            Request a Detailed Quote
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={outcome === "submitting"}
            onClick={() => void handleSubmit("email_me_my_estimate")}
          >
            Email Me My Estimate
          </button>
        </div>
      </form>
    </div>
  );
}
